import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getSaleById, createSale, updateSale, getProducts } from "../services/api";
import { ICONS } from "../icons";
import Button from "../components/UI/Button";
import FloatingInput from "../components/UI/FloatingInput";
import ToggleSwitch from "../components/UI/ToggleSwitch";
import { notify } from "../utils/notify";
import PageHeader from "../components/UI/PageHeader";
import Select from "react-select";
import { useTranslation } from "../context/I18nContext";

// A Sale is a bulk/group discount campaign: a flat percentage off every
// product it's attached to, for an optional date range — leaving both
// dates blank means the sale never expires. See models.Sale and
// services.EffectiveDiscount for how this ranks against a product's own
// single/quantity-tier discount (both of those win over a Sale) and a
// BundleOffer (which excludes a product from every other discount,
// including this one, entirely).
export default function AddEditSale() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const { t } = useTranslation("discounts");
  const { t: tCommon } = useTranslation("common");

  const [productOptions, setProductOptions] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [form, setForm] = useState({ name: "", discount_percent: "", start_date: "", end_date: "", is_active: true });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    getProducts(token)
      .then((res) => setProductOptions((res || []).map((p) => ({ value: p.id, label: `${p.name} (${p.sku})` }))))
      .catch(() => notify.error(t("notify_load_products_failed", "Failed to load products")));

    if (id) {
      getSaleById(token, id).then((s) => {
        setForm({
          name: s.name || "",
          discount_percent: s.discount_percent ?? "",
          start_date: s.start_date ? s.start_date.slice(0, 10) : "",
          end_date: s.end_date ? s.end_date.slice(0, 10) : "",
          is_active: s.is_active !== undefined ? s.is_active : true,
        });
        setSelectedProducts((s.products || []).map((p) => ({ value: p.id, label: `${p.name} (${p.sku})` })));
      }).catch(() => notify.error(t("notify_load_failed", "Failed to load sale")));
    }
  }, [id, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const setField = (key) => (e) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handleSubmit = async () => {
    setErrors({});
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = t("validation_name_required", "Name is required");
    const pct = Number(form.discount_percent);
    if (!form.discount_percent || pct <= 0 || pct > 100) newErrors.discount_percent = t("validation_discount_percent_range", "Enter a percentage between 1 and 100");
    if (selectedProducts.length === 0) newErrors.products = t("validation_products_required", "Select at least one product");
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      notify.error(tCommon("validation_fill_required_fields", "Please fill in the required fields correctly"));
      return;
    }

    setLoading(true);
    const payload = {
      name: form.name.trim(),
      discount_percent: pct,
      start_date: form.start_date,
      end_date: form.end_date,
      is_active: form.is_active,
      product_ids: selectedProducts.map((p) => p.value),
    };

    try {
      if (id) {
        await updateSale(token, id, payload);
        notify.success(t("notify_updated", "Sale updated successfully"));
      } else {
        await createSale(token, payload);
        notify.success(t("notify_created", "Sale created successfully"));
      }
      navigate("/discounts");
    } catch (err) {
      notify.error(err.data?.error || err.message || tCommon("error_generic", "Something went wrong"));
    } finally {
      setLoading(false);
    }
  };

  const customSelectStyles = {
    control: (base) => ({
      ...base,
      padding: '2px',
      borderRadius: '12px',
      borderColor: '#f3f4f6',
      backgroundColor: '#f9fafb80',
      '&:hover': { borderColor: 'var(--primary-color)' },
      boxShadow: 'none',
    }),
    multiValue: (base) => ({ ...base, backgroundColor: 'var(--primary-color)', color: 'white', borderRadius: '8px' }),
    multiValueLabel: (base) => ({ ...base, color: 'white' }),
  };

  return (
    <div className="p-4 md:p-8 max-w-3xl mx-auto">
      <PageHeader className="flex items-center gap-4 mb-8 border-b border-gray-100 pb-6">
        <button onClick={() => navigate("/discounts")} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400">
          <ICONS.ChevronLeft className="w-6 h-6 rtl:rotate-180" />
        </button>
        <div>
          <h2 className="text-2xl font-black text-gray-800 tracking-tight">{id ? t("edit_title", "Edit Sale") : t("add_title", "Add New Sale")}</h2>
          <p className="text-gray-400 text-sm italic font-medium">{t("form_subtitle", "A percentage off a hand-picked set of products, for an optional date range")}</p>
        </div>
      </PageHeader>

      <div className="bg-white p-6 md:p-8 rounded-[2.5rem] border border-gray-100 shadow-sm">
        <div className="space-y-6">
          <FloatingInput label={t("field_name_label", "Sale Name")} value={form.name} onChange={setField("name")} error={errors.name} icon={ICONS.Discounts} />
          <FloatingInput label={t("field_discount_percent_label", "Discount Percent")} type="number" min="1" max="100" value={form.discount_percent} onChange={setField("discount_percent")} error={errors.discount_percent} />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FloatingInput label={t("field_start_date_label", "Start Date (optional)")} type="date" value={form.start_date} onChange={setField("start_date")} />
            <FloatingInput label={t("field_end_date_label", "End Date (optional)")} type="date" value={form.end_date} onChange={setField("end_date")} />
          </div>
          <p className="text-[10px] text-gray-400 ms-4 italic -mt-4">
            {t("hint_no_expiry", "* Leave both dates blank for a sale that never expires.")}
          </p>

          <div className="space-y-3">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ms-4">
              {t("products_included_label", "Products Included")}
            </label>
            <Select
              options={productOptions}
              value={selectedProducts}
              onChange={setSelectedProducts}
              isMulti
              placeholder={t("products_select_placeholder", "Select one or more products...")}
              styles={customSelectStyles}
              className="text-sm"
            />
            {errors.products && <p className="text-[10px] text-red-500 font-bold ms-4 uppercase">{errors.products}</p>}
            <p className="text-[10px] text-gray-400 ms-4 italic">
              {t("hint_products_precedence", "* A product with its own single discount or quantity-tier discount active ignores this Sale. A product currently in an active Bundle Offer ignores it too.")}
            </p>
          </div>

          <ToggleSwitch
            label={tCommon("status_active", "Active")}
            checked={form.is_active}
            onChange={(val) => setForm((prev) => ({ ...prev, is_active: val }))}
          />

          <div className="flex gap-4 pt-6 border-t border-gray-50">
            <Button
              variant="primary"
              onClick={handleSubmit}
              loading={loading}
              className="flex-grow shadow-lg shadow-blue-500/10"
              icon={id ? ICONS.Save : ICONS.Plus}
            >
              {id ? t("btn_update_sale", "Update Sale") : t("btn_create_sale", "Create Sale")}
            </Button>
            <Button variant="secondary" onClick={() => navigate("/discounts")}>{tCommon("btn_cancel", "Cancel")}</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
